import { useEffect, useState } from "react";
import { useTenant } from "../../../Contexts/TenantContext";
import { TenantTechnicalAssistance } from "../../../Models/TenantTechnicalAssistance";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import Footer from "../../../Components/Footer/Footer";
import PublicPageTitle from "../../../Components/PublicPageTitle/PublicPageTitle";

const UserTechnicalAssistancePage = () => {
  const { fetchTenantTechnicalAssistance } = useTenant();

  const [data, setData] = useState<TenantTechnicalAssistance | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await fetchTenantTechnicalAssistance();
        setData(result);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderSection = (title?: string | null, description?: string | null) => {
    if (!title && !description) {
      return null;
    }
    return (
      <section className="space-y-2">
        {/* Stores type these titles by hand, so the ending is normalized here */}
        {title && <h2 className="text-base sm:text-lg font-semibold text-gray-800">{title.replace(/\s*:\s*$/, "")}</h2>}
        {description && <p className="text-gray-700 whitespace-pre-line">{description}</p>}
      </section>
    );
  };

  const SkeletonBlock = ({ height = 16, width = "100%" }: { height?: number | string; width?: number | string }) => (
    <div className="animate-pulse bg-gray-200 rounded w-full" style={{ height, maxWidth: width }} />
  );

  const renderSkeleton = () => (
    <div className="space-y-8 max-w-5xl mx-auto">
      <SkeletonBlock height={32} width={240} />
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <SkeletonBlock key={index} height={16 + (index % 2) * 4} />
        ))}
      </div>
      <SkeletonBlock height={220} />
    </div>
  );

  return (
    <div className={`flex flex-col min-h-screen bg-white`}>
      <SmartNavbar />
      <main className={`flex-1 bg-white`}>
        <div className="container mx-auto page-gutter pt-16 md:pt-[7.5rem] pb-14 sm:pb-16 space-y-10">
          {loading ? (
            renderSkeleton()
          ) : data ? (
            <div className={`space-y-8 mx-auto max-w-5xl`}>
              <PublicPageTitle>
                {data.heroTitle || "Assistência Técnica"}
              </PublicPageTitle>

              <div className={`space-y-7 leading-relaxed md:text-justify text-sm sm:text-lg text-gray-800`}>
                {renderSection(data.introTitle, data.introDescription)}

                {data.image?.url && (
                  <div className="w-full flex justify-center pt-2">
                    <img
                      src={data.image.url}
                      alt={"Assistência técnica"}
                      className={`w-full object-cover max-w-4xl max-h-[220px] rounded shadow-sm`}
                    />
                  </div>
                )}

                {renderSection(data.section1Title, data.section1Description)}
                {renderSection(data.section2Title, data.section2Description)}
                {renderSection(data.section3Title, data.section3Description)}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-600">
              {"Nenhuma informação de assistência técnica disponível."}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserTechnicalAssistancePage;
